/**
 * ZDANIE ZAMIAST KODU — tłumaczenie odmowy serwera na język człowieka.
 *
 * ⚠️ W TYM PLIKU ŻYJĄ DWIE FUNKCJE I TO JEST ŚWIADOME. Historia jest pouczająca, więc zostaje
 * zapisana: 2026-09-11 (F-1) dopisałem tu `opiszBladSerwera` i **nadpisałem plik w całości**,
 * kasując istniejące `zdanieOBledzie` razem z jego mapą statusów. Skutek: `MyDeptEvaluationPage`
 * wołała funkcję, której już nie było — czyli przy KAŻDYM błędzie serwera na ocenie pracownika
 * leciało „zdanieOBledzie is not a function" zamiast komunikatu. Złapała to dopiero pełna suita
 * frontu (zapadka `bladSerwera.test.js` z 2026-09-05), bo przy tamtej zmianie uruchomiłem
 * wyłącznie testy licencji. Wniosek na przyszłość jest prosty i kosztował jedną regresję:
 * **dopisując eksport do istniejącego pliku, nie nadpisuj go — dopisz.**
 *
 * Obie funkcje robią to samo z innym ciężarem i celowo się różnią:
 *
 *  • `zdanieOBledzie` [zgłoszenie właściciela 2026-09-05 noc, „/moje-cele i /moj-dzial/ocena-pracownika
 *    NIE DZIAŁAJĄ"] — NIGDY nie pokazuje `err.message`. Powód: gołe `Forbid()` daje 403 z pustym
 *    ciałem, a wtedy `err.message` to napis biblioteki („Request failed with status code 403"),
 *    czyli surowy kod techniczny na ekranie. Kolejność: zdanie serwera → mapa statusów → zdanie
 *    wołającego.
 *
 *  • `opiszBladSerwera` [F-1 2026-09-11, „Próba dodania licencji zwraca błąd"] — POKAZUJE treść
 *    odpowiedzi, łącznie z `ProblemDetails.errors` z walidacji, bo tam dokładnie siedzi powód
 *    („Typ licencji jest wymagany"). `err.message` przepuszcza tylko wtedy, gdy NIE jest napisem
 *    biblioteki — komunikaty klienta (np. przerwany upload) bywają jedyną informacją, jaką mamy.
 *
 * Nowy kod: użyj `opiszBladSerwera`. `zdanieOBledzie` zostaje dla stron, które już na nim stoją,
 * i dla własnej zapadki — przepisywanie ich „przy okazji" to ryzyko bez zysku.
 */

const WG_STATUSU = {
    400: "Serwer odrzucił dane formularza. Sprawdź pola i spróbuj ponownie.",
    401: "Sesja wygasła. Odśwież stronę.",
    403: "Nie masz uprawnień do tej operacji.",
    404: "Nie znaleziono tego elementu — mógł zostać usunięty w międzyczasie.",
    409: "Ktoś zmienił to w międzyczasie. Odśwież i spróbuj ponownie.",
    413: "Plik jest za duży.",
    422: "Dane nie przeszły walidacji po stronie serwera.",
};

/**
 * @param {any} err  błąd z axiosa
 * @param {string} awaryjny  zdanie, gdy serwer nie powiedział nic i status jest nieznany
 * @returns {string} zdanie po polsku — nigdy kod techniczny
 */
export function zdanieOBledzie(err, awaryjny = "Nie udało się wykonać operacji. Spróbuj ponownie.") {
    const dane = err?.response?.data;
    const zSerwera = dane?.message || dane?.error || dane?.title;
    if (typeof zSerwera === "string" && zSerwera.trim()) return zSerwera;

    const status = err?.response?.status;
    if (status && WG_STATUSU[status]) return WG_STATUSU[status];
    if (status >= 500) return "Błąd po stronie serwera. Jeśli wróci, zgłoś to do IT.";

    // Brak odpowiedzi w ogóle = sieć. Też nie pokazujemy komunikatu biblioteki.
    if (err && !err.response) return "Brak połączenia z serwerem. Sprawdź sieć i spróbuj ponownie.";
    return awaryjny;
}

const tekst = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);

/**
 * [F-1 2026-09-11] Jedno miejsce, w którym odpowiedź błędu z serwera zamienia się w zdanie
 * dla człowieka — z TREŚCIĄ odpowiedzi, nie tylko z jej statusem.
 *
 * <b>Po co powstała.</b> Właściciel zgłosił: „Próba dodania licencji zwraca błąd.” Serwer
 * odpowiadał precyzyjnie („Typ licencji jest wymagany.”), a szuflada licencji pokazywała
 * `showToast("Nie udało się dodać licencji.")` — treść odpowiedzi lądowała wyłącznie
 * w `console.error`. Bez powodu w komunikacie usterka wygląda na awarię aplikacji, a nie na
 * brakujące pole, i nie da się jej zgłosić inaczej niż zdaniem „nie działa”.
 *
 * Wariant tej funkcji istniał już w `pages/eteczka/eteczkaApi.js`, ale z komunikatami
 * przypisanymi do akt pracowniczych („Nie masz dostępu do akt tej osoby”), więc reszta
 * aplikacji nie mogła go użyć. Tu jest wersja neutralna; e-teczka nadaje własne brzmienie
 * przez parametr `komunikaty`.
 */
export const opiszBladSerwera = (err, domyslny = "Nie udało się wykonać operacji.", komunikaty = {}) => {
    const kod = err?.response?.status;

    if (kod === 413) return komunikaty[413] || "Serwer odrzucił plik jako zbyt duży (limit po stronie serwera WWW).";
    if (kod === 401) return komunikaty[401] || "Sesja wygasła — zaloguj się ponownie i powtórz czynność.";

    /* [BI-2 2026-09-20] 🔴 ODMOWA Z POWODEM PRZESTAWAŁA BYĆ ODMOWĄ Z POWODEM DOKŁADNIE TUTAJ.

       403 był obsługiwany PRZED sięgnięciem po treść odpowiedzi, więc serwer mógł napisać
       „Ten komputer stoi w grupie przypisanej do innej spółki — zdalne połączenie obsługuje serwis
       tamtej firmy”, a człowiek przy ekranie i tak widział „Nie masz uprawnień do tej operacji.”
       Zdanie ogólne nie mówi ani CO jest nie tak, ani CO z tym zrobić — a przy podziale na spółki to
       jest różnica między „poproś o klucz” a „przekaż sprawę tamtej obsłudze”.

       Skrót zostaje dla 403 BEZ TREŚCI (gołe `Forbid()`), bo tam alternatywą jest napis biblioteki
       („Request failed with status code 403”) — czyli dokładnie ta wada, dla której ten plik powstał.
       Nadpisanie z `komunikaty[403]` nadal ma pierwszeństwo: strona, która zna swój kontekst lepiej
       niż serwer, może je narzucić. */
    if (kod === 403 && komunikaty[403]) return komunikaty[403];

    const dane = err?.response?.data;

    if (kod === 403 && !tekst(dane) && !(dane && typeof dane === "object"))
        return "Nie masz uprawnień do tej operacji.";
    // (403 z ciałem, z którego nic nie da się wyłuskać, wraca tym samym zdaniem na samym końcu.)

    if (tekst(dane)) return tekst(dane);

    if (dane && typeof dane === "object") {
        /* [zgłoszenie właściciela 2026-09-11] KOLEJNOŚĆ MA ZNACZENIE, a była ODWROTNA.

           `ValidationProblemDetails` niesie OBA pola naraz: `title` = „One or more validation errors
           occurred." (stały, angielski napis biblioteki) ORAZ `errors` z prawdziwym powodem
           („Pole ActivationEmail nie jest poprawnym adresem e-mail"). Sprawdzanie `title` PRZED
           `errors` sprawiało, że człowiek widział WYŁĄCZNIE ten angielski napis — przy każdej
           odmowie walidacji, w całej aplikacji.

           Zmierzone na produkcji 2026-09-11: `POST /api/itam/licenses` kończył się 400
           z kompletem powodów w `errors`, a właściciel zgłosił to jako „nie da się dodać",
           bo nic innego nie miał na ekranie. Serwer mówił prawdę — to interfejs ją gubił.

           Dlatego `errors` idzie PIERWSZE: to jedyne pole, które mówi, CO poprawić. */
        if (dane.errors && typeof dane.errors === "object") {
            const powody = Object.entries(dane.errors)
                .flatMap(([, lista]) => (Array.isArray(lista) ? lista : [lista]))
                .map((x) => tekst(x))
                .filter(Boolean);

            // Kilka pól naraz wymieniamy w całości — poprawienie jednego i tak nie wystarczy,
            // a lista dwóch zdań mieści się w toaście.
            if (powody.length) return powody.join(" ");
        }

        if (tekst(dane.error)) return tekst(dane.error);
        if (tekst(dane.message)) return tekst(dane.message);

        /* `title` jako OSTATNIA deska ratunku i tylko wtedy, gdy nie jest stałym napisem biblioteki:
           pokazanie go zamiast powodu to pokazanie kodu technicznego tam, gdzie ma być zdanie. */
        const tytul = tekst(dane.title);
        if (tytul && !/^One or more validation errors/i.test(tytul)) return tytul;
    }

    // Brak odpowiedzi = żądanie w ogóle nie doszło. To INNA przyczyna niż odmowa serwera
    // i człowiek musi ją odróżnić — inaczej będzie klikał „Zapisz” przy zerwanym łączu.
    if (err?.request && !err?.response) {
        return "Serwer nie odpowiedział — sprawdź połączenie i spróbuj ponownie.";
    }

    if (err?.message && !/^Request failed/i.test(err.message)) return err.message;

    // [BI-2] 403, z którego nie dało się wyłuskać ani jednego zdania (np. `data: {}`) — wracamy do
    // komunikatu ogólnego. Zdanie wołającego („Nie udało się zapisać…") byłoby tu myląco łagodne:
    // to nie jest usterka zapisu, tylko odmowa dostępu.
    if (kod === 403) return "Nie masz uprawnień do tej operacji.";

    return domyslny;
};

export default opiszBladSerwera;
